import Image from "next/image"
import { usePathname, useSearchParams } from "next/navigation"
import { Button } from "@ui/shad"

import { useEmailIntegrationAccess, useGetAuthUriMutation } from "@/hooks/api/email_integrations"
import { useEmailConnectNotice } from "@/components/integrations/email-connect-notice"

export default function EmailIntegration({
  backgroundComponent,
  loadingSkeleton,
  children,
  title = "Connect your email",
}: {
  backgroundComponent: React.ReactNode
  loadingSkeleton?: React.ReactNode
  children: React.ReactNode
  title?: string
}) {
  const authMutation = useGetAuthUriMutation()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { hasEmailIntegrationAccess, isLoadingAccess } = useEmailIntegrationAccess()

  async function handleProviderConnect(provider: string) {
    const returnParams = new URLSearchParams(searchParams.toString())
    returnParams.set("email", "connected")
    const { auth_uri } = await authMutation.mutateAsync({
      provider,
      returnTo: `${pathname}?${returnParams.toString()}`,
      source: "client",
    })
    window.location.href = auth_uri
  }

  const { requestConnect, noticeDialog } = useEmailConnectNotice(handleProviderConnect)

  if (isLoadingAccess) {
    return <>{loadingSkeleton}</>
  }

  if (hasEmailIntegrationAccess) {
    return children
  }

  return (
    <>
      <div className="absolute top-0 left-0 w-full" style={{ filter: "blur(6px)" }}>
        {backgroundComponent}
      </div>
      <div className="bg-background absolute top-0 left-0 z-2 flex h-full w-full opacity-80" />
      <div className="absolute top-0 left-0 z-3 flex h-full w-full flex-col items-center justify-center p-4">
        <span className="text-2xl font-semibold">{title}</span>
        <span className="text-muted-foreground text-sm">Choose one of the providers below to get started.</span>
        <div className="mt-4 flex items-center gap-2">
          <Button variant="outline" onClick={() => requestConnect("google")}>
            <Image src="/google.svg" alt="Google" width={16} height={16} />
            Google
          </Button>
          <Button variant="outline" onClick={() => requestConnect("microsoft")}>
            <Image src="/outlook.svg" alt="Outlook" width={16} height={16} />
            Outlook
          </Button>
        </div>
      </div>
      {noticeDialog}
    </>
  )
}
